import React from 'react'
import { motion } from 'framer-motion'

const HeroCallToActionSection: React.FC = () => {
  return (
    <section 
      className="relative overflow-hidden"
      style={{ 
        backgroundColor: 'rgb(30, 29, 28)', // Exact Figma dark background
        height: '600px'
      }}
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 w-full h-full bg-cover bg-center"
        style={{
          backgroundImage: `url('/hero-image.png')`, // Using existing hero image
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transform: 'translateY(-60px)', // Offset similar to Figma positioning
          height: 'calc(100% + 120px)'
        }}
      >
        {/* Dark Overlay */}
        <div 
          className="absolute inset-0"
          style={{ backgroundColor: 'rgba(30, 29, 28, 0.75)' }}
        />
      </div>

      {/* Content Container */}
      <div className="relative z-10 max-w-[1200px] mx-auto h-full flex items-center justify-center px-4">
        
        {/* Left Vertical Divider */}
        <div 
          className="absolute left-0 top-0 w-px h-full hidden lg:block"
          style={{ 
            backgroundColor: 'white',
            left: 'calc(50% - 182px)'
          }}
        />

        {/* Right Vertical Divider */}
        <div 
          className="absolute right-0 top-0 w-px h-full hidden lg:block"
          style={{ 
            backgroundColor: 'white',
            right: 'calc(50% - 182px)'
          }}
        />

        {/* Main Content */}
        <div className="text-center text-white max-w-4xl">
          
          {/* Main Headline */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-normal leading-tight mb-8"
              style={{ 
                color: 'white',
                fontWeight: '400',
                lineHeight: '1.2'
              }}
            >
              Are you ready to experience <br />
              effortless homeownership?
            </h1>
          </motion.div>

          {/* Supporting Text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-16"
          >
            <p 
              className="text-lg md:text-xl leading-relaxed max-w-2xl mx-auto"
              style={{ color: 'white' }}
            >
              We maintain your home so you don&apos;t have to. Enjoy a perfectly managed home—
              be informed, not involved—and focus on what matters most.
            </p>
          </motion.div>

          {/* CTA Buttons Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col md:flex-row gap-8 md:gap-16 justify-center items-center"
          >
            
            {/* Left CTA */}
            <div className="text-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="text-lg font-medium tracking-wide transition-all duration-200 hover:opacity-80"
                style={{ color: 'white' }}
              >
                Request Your Invitation
              </motion.button>
              
              {/* Underline */}
              <div 
                className="w-32 h-0.5 mx-auto mt-3"
                style={{ backgroundColor: 'white' }}
              />
            </div>

            {/* Right CTA */}
            <div className="text-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="text-lg font-medium tracking-wide transition-all duration-200 hover:opacity-80"
                style={{ color: 'white' }}
              >
                Schedule a Consultation
              </motion.button>
              
              {/* Underline */}
              <div 
                className="w-32 h-0.5 mx-auto mt-3"
                style={{ backgroundColor: 'white' }}
              />
            </div>
          </motion.div>
        </div>

        {/* Decorative Elements - Additional Vertical Lines */}
        <div className="absolute inset-0 hidden xl:block pointer-events-none">
          {/* Inner Left Line */}
          <div 
            className="absolute top-0 w-px h-full"
            style={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              left: 'calc(50% - 300px)'
            }}
          />
          {/* Inner Right Line */}
          <div 
            className="absolute top-0 w-px h-full"
            style={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              right: 'calc(50% - 300px)'
            }}
          />
        </div>
      </div>

      {/* Mobile Layout Optimization */}
      <div className="lg:hidden absolute inset-0 flex items-center justify-center px-4">
        <div className="text-center text-white max-w-lg">
          <h1 className="text-3xl font-normal leading-tight mb-6" style={{ color: 'white' }}>
            Are you ready to experience effortless homeownership?
          </h1>
          <p className="text-base leading-relaxed mb-8" style={{ color: 'white' }}>
            With The Hudson Club, you can enjoy a perfectly managed home 
            that allows you to focus on what matters most.
          </p>
          
          {/* Mobile CTAs */}
          <div className="space-y-6">
            <div>
              <button className="text-base font-medium" style={{ color: 'white' }}>
                Request Your Invitation
              </button>
              <div className="w-24 h-0.5 mx-auto mt-2" style={{ backgroundColor: 'white' }} />
            </div>
            <div>
              <button className="text-base font-medium" style={{ color: 'white' }}>
                Schedule a Consultation
              </button>
              <div className="w-24 h-0.5 mx-auto mt-2" style={{ backgroundColor: 'white' }} />
            </div>
          </div>
        </div>
      </div>

      {/* Ambient Light Effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full opacity-10"
          style={{ 
            background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)'
          }}
        />
      </div>
    </section>
  )
}

export default HeroCallToActionSection 